import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Table, Button, message } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import styled from 'styled-components';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { AmountField, ExportInFile, Space } from '~/components';
import { AccountBalance } from '~/types';
import { DataType, getAccountsBalances } from '~/api/accounts';
import { useMutation, useQuery } from 'react-query';
import { Longdash } from '~/utils';
import { getBalancesExportResult } from '~/api/accounts';
import {
  useCreateBalancesExportTask,
  useBalancesExportStatus,
  useBalancesExportCheck,
} from '~/hooks/accounts';
import layout from '~/hooks/layout';
import { usePath } from '~/hooks';
import BalanceCard from './BalanceCard';

interface Props {
  className?: string;
}

const Balances: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation('accounts');
  const { t: td } = useTranslation();

  const {
    params: { filter },
  } = usePath();

  const buttonRef = useRef<HTMLButtonElement>(null);

  const [loading, setLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [data, setData] = useState<DataType>({
    cursors: {},
    balances: [],
  });

  const filterKey = useMemo(() => JSON.stringify(filter || {}), [filter]);

  const query = useMemo(
    () => ({
      accountId: filter?.accountId || undefined,
      bankingDayFrom: filter?.bankingDayFrom || undefined,
      bankingDayTo: filter?.bankingDayTo || undefined,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterKey]
  );

  const { data: balancesData, isFetching: balancesFetching } = useQuery(
    ['balances', query],
    () => getAccountsBalances({ limit: 30, filters: { ...query } }),
    {
      onSuccess: (result) => {
        setData(result || { cursors: {}, balances: [] });
        setHasNextPage(!!result?.cursors?.next);
      },
    }
  );

  const dynamicBalanceKeys = useMemo(() => {
    if (!balancesData?.balances?.length) return [];
    const keys = new Set<string>();
    balancesData?.balances.forEach((row) => {
      const balances = (row as any).balances || {};
      Object.keys(balances).forEach((k) => keys.add(k));
    });
    return Array.from(keys);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balancesData?.balances]);

  const columns: ColumnsType<AccountBalance> = useMemo(() => {
    const base: ColumnsType<AccountBalance> = [
      {
        title: t('account'),
        dataIndex: 'accountNumber',
        key: 'accountNumber',
        render: (value) => <span>{value}</span>,
      },
      {
        title: t('bankingDay'),
        dataIndex: 'bankingDay',
        key: 'bankingDay',
        render: (_, record) => <span>{moment(record.bankingDay).format('LL')}</span>,
      },
    ];

    const dynamic = dynamicBalanceKeys.map((key) => ({
      title: td(key),
      dataIndex: ['balances', key] as any,
      key,
      render: (value: number, record: AccountBalance) =>
        value !== undefined ? (
          <AmountField amount={{ value, currency: record?.currency }} />
        ) : (
          Longdash
        ),
      align: 'right' as const,
    }));

    return [...base, ...dynamic];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, dynamicBalanceKeys]);

  const { mutateAsync: fetchBalances } = useMutation(getAccountsBalances);

  const loadMore = useCallback(async () => {
    if (!hasNextPage || loading) return;
    setLoading(true);
    try {
      const res = await fetchBalances({
        limit: 30,
        next: data?.cursors?.next,
        filters: { ...query },
      });

      setData((prev) => ({
        cursors: { next: res?.cursors?.next },
        balances: [...(prev.balances || []), ...(res?.balances || [])],
      }));
      setHasNextPage(!!res?.cursors?.next);
    } catch (error: any) {
      void message.error(error?.message);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.cursors?.next, query, hasNextPage, loading, fetchBalances]);

  const isMobile = layout.useIsMobile();

  return (
    <div className={className}>
      <div className="Balances-Export">
        <ExportInFile
          getExportResult={getBalancesExportResult}
          useExportCheck={useBalancesExportCheck}
          useCreateExportTask={useCreateBalancesExportTask}
          useExportStatus={useBalancesExportStatus}
          filePrefix="balances"
          filters={filter}
        />
      </div>
      {isMobile ? (
        <Space direction="vertical" size={16}>
          {data?.balances?.map((item) => (
            <BalanceCard key={item.id} record={item} dynamicBalanceKeys={dynamicBalanceKeys} />
          ))}
        </Space>
      ) : (
        <Table<AccountBalance>
          columns={columns}
          dataSource={data?.balances}
          loading={balancesFetching || loading}
          rowKey={(record) => record.id}
          pagination={false}
        />
      )}
      {data?.balances?.length > 0 && hasNextPage && (
        <div className="Balances-LoadMore">
          <Button
            ref={buttonRef}
            loading={loading}
            disabled={!hasNextPage}
            onClick={() => {
              buttonRef.current?.blur();
              void loadMore();
            }}
          >
            {hasNextPage ? t('loadMore') : t('allLoaded')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default styled(Balances)`
  .Balances-LoadMore {
    padding: 8px;
    display: flex;
    justify-content: center;
  }
  .Balances-Export {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 16px;
  }
`;
